'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { CommonProps } from '../types.js';
import { Text } from '../typography/text.js';

export interface CardProps extends CommonProps {
  /** The contents of the card. */
  children: ReactNode;
}

function Card({ children, UNSAFE_className, UNSAFE_style, ...props }: CardProps, ref: ForwardedRef<HTMLDivElement>) {
  return (
    <div {...props} ref={ref} className={clsx('cim-card', UNSAFE_className)} style={UNSAFE_style}>
      {children}
    </div>
  );
}

/**
 * Groups related blocks of content into a single unit.
 *
 * See [card usage guidelines](https://ui.cimpress.io/components/card/).
 */
const _Card = withStyleProps(forwardRef(Card), 'Card');

export { _Card as Card };

export interface CardContentProps extends CommonProps {
  /** The content of this block. */
  children: ReactNode;
  /**
   * Whether the block's content should stretch to the edges of the block.
   * @default false
   */
  fullBleed?: boolean;
}

/** Renders a single block of content within `Card`. */
export function CardContent({
  children,
  UNSAFE_className,
  UNSAFE_style,
  fullBleed = false,
  ...props
}: CardContentProps) {
  return (
    <div
      {...props}
      className={clsx('cim-card-content', UNSAFE_className)}
      style={UNSAFE_style}
      data-full-bleed={fullBleed || undefined}
    >
      {children}
    </div>
  );
}

CardContent.displayName = 'CardContent';

export interface CardHeaderProps extends CommonProps {
  /** The icon to display at the start of the header. */
  iconStart?: ReactNode;
  /**
   * The title of the card. Optionally, you can pass a React node to render a link.
   *
   * @example
   * <CardHeader title="Card title" />
   * <CardHeader title={<Link href="#">Card title</Link>} />
   */
  title: ReactNode;
  /**
   * The description of the card. Optionally, you can pass a React node to render rich text.
   *
   * @example
   * <CardHeader title="Card title" description="Card subtitle" />
   * <CardHeader title="Card title" description={<Link href="#">Card subtitle</Link>} />
   */
  description?: ReactNode;
  /**
   * The tools to render at the end of the header. Usually a menu, icon button, or badge. For buttons, use the "small" size variant.
   *
   * @example
   * <CardHeader title="Card title" tools={<IconButton size="small" icon={<IconRatingStar />} aria-label="Favorite" />} />
   */
  tools?: ReactNode;
}

/** Renders a header within a `Card`. */
export function CardHeader({
  UNSAFE_className,
  UNSAFE_style,
  iconStart,
  title,
  description,
  tools,
  ...props
}: CardHeaderProps) {
  return (
    <div {...props} className={clsx('cim-card-header', 'cim-card-content', UNSAFE_className)} style={UNSAFE_style}>
      {iconStart}

      <div className="cim-card-header-text">
        <Text as="h2" variant="body-semibold" tone="base">
          {title}
        </Text>
        {description && (
          <Text as="p" variant="small" tone="subtle">
            {description}
          </Text>
        )}
      </div>

      <div className="cim-card-header-tools">{tools}</div>
    </div>
  );
}

CardHeader.displayName = 'CardHeader';
